/**
 * OnboardingPage - Wizard 4 etapes pour connecter le tenant M365 du client.
 *
 * Etapes :
 *  1. Bienvenue + explication
 *  2. Consent : on ouvre la fenetre MS dans un nouvel onglet
 *  3. Test : on appelle /api/onboarding/test-connection
 *  4. Done : redirect vers /
 *
 * Auto-detection : si /api/onboarding/status retourne connected=true on jump
 * directement a l etape Test (ou Done si tout est OK).
 *
 * Le user revient sur cette page apres click sur le bouton MS consent +
 * redirect /api/oauth/m365/callback → /onboarding?consent=ok
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Body1,
  Button,
  Card,
  Caption1,
  Spinner,
  Title2,
  Title3,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { PageHeader } from '../components/PageHeader';
import { onboardingApi, type OnboardingStatus, type TestConnectionResponse } from '../api/onboardingApi';

type Step = 'intro' | 'consent' | 'test' | 'done';

export function OnboardingPage() {
  const { t }       = useTranslation();
  const [params]    = useSearchParams();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus]   = useState<OnboardingStatus | null>(null);
  const [step, setStep]       = useState<Step>('intro');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [test, setTest]       = useState<TestConnectionResponse | null>(null);

  // Lit le status au mount + apres retour du callback consent
  useEffect(() => {
    void loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detecte les query params au retour du callback MS
  useEffect(() => {
    const consent = params.get('consent');
    const err     = params.get('error');
    if (consent === 'ok') {
      setStep('test');
      // Lance automatiquement le test apres consent
      void runTest();
    } else if (consent === 'tenant_locked') {
      // Backend a refuse un rebinding vers un autre tenant M365 que celui
      // initialement lie. Message clair, pas de retry possible sans support.
      setError(t('onboarding.wizard.errorTenantLocked'));
      setStep('intro');
    } else if (err) {
      setError(decodeURIComponent(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  async function loadStatus() {
    try {
      const s = await onboardingApi.status();
      setStatus(s);
      // ?force=1 : on reste sur l etape intro meme si deja connected, pour
      // permettre de re-consenter (ex: nouvelles permissions Graph ajoutees,
      // changement de tenant, etc.).
      const force = params.get('force') === '1';
      if (s.connected && !force) {
        // Deja consent : on peut soit aller direct au test, soit done si test OK recemment
        if (s.lastTestSuccess) setStep('done');
        else setStep('test');
      }
    } catch (e) {
      setError((e as Error)?.message ?? t('onboarding.wizard.errorLoadFailed'));
    }
  }

  async function startConsent() {
    setBusy(true); setError(null);
    try {
      const r = await onboardingApi.startConsent();
      // Ouvre la fenetre MS dans un NOUVEL onglet pour ne pas perdre notre session
      window.open(r.consentUrl, '_blank', 'noopener,noreferrer');
      setStep('consent');
    } catch (e) {
      setError((e as Error)?.message ?? t('onboarding.wizard.errorConsentUrl'));
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    setBusy(true); setError(null); setTest(null);
    try {
      const r = await onboardingApi.testConnection();
      setTest(r);
      if (r.success) {
        setStep('done');
        await loadStatus();
        // Invalide le cache global pour que le banner "Action requise" du
        // Layout disparaisse immediatement (et que les autres pages voient
        // tenant=connecte sans avoir a F5).
        await queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
      }
    } catch (e) {
      setError((e as Error)?.message ?? t('onboarding.wizard.errorTestFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title={t('onboarding.wizard.pageTitle')}
        description={t('onboarding.wizard.pageDescription')}
      />

      {/* Stepper visuel */}
      <Stepper current={step} />

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>{t('onboarding.wizard.errorOccurred')}</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      <div style={{ marginTop: 16 }}>
        {step === 'intro' && (
          <IntroStep onStart={startConsent} busy={busy} status={status} />
        )}
        {step === 'consent' && (
          <ConsentStep onTest={runTest} busy={busy} />
        )}
        {step === 'test' && (
          <TestStep onTest={runTest} busy={busy} test={test} />
        )}
        {step === 'done' && (
          <DoneStep status={status} onGoToDashboard={() => navigate('/')} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper visual (4 ronds + ligne)
// ---------------------------------------------------------------------------
function Stepper({ current }: { current: Step }) {
  const { t } = useTranslation();
  const steps: { id: Step; label: string }[] = [
    { id: 'intro',   label: t('onboarding.wizard.stepper.intro') },
    { id: 'consent', label: t('onboarding.wizard.stepper.consent') },
    { id: 'test',    label: t('onboarding.wizard.stepper.test') },
    { id: 'done',    label: t('onboarding.wizard.stepper.done') },
  ];
  const currentIdx = steps.findIndex(s => s.id === current);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '24px 0', flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div
            style={{
              width: 32, height: 32, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 13,
              background: i <= currentIdx ? tokens.colorBrandBackground : tokens.colorNeutralBackground3,
              color:      i <= currentIdx ? '#fff' : tokens.colorNeutralForeground3,
              border:     i === currentIdx ? `2px solid ${tokens.colorBrandStroke1}` : 'none',
            }}
          >
            {i < currentIdx ? '✓' : i + 1}
          </div>
          <span style={{
            fontSize: 13, fontWeight: i === currentIdx ? 600 : 400,
            color: i <= currentIdx ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground3,
          }}>
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <div style={{
              width: 32, height: 2,
              background: i < currentIdx ? tokens.colorBrandBackground : tokens.colorNeutralStroke2,
              margin: '0 8px',
            }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Etape 1 - Intro
// ---------------------------------------------------------------------------
function IntroStep({ onStart, busy, status }: { onStart: () => void; busy: boolean; status: OnboardingStatus | null }) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: 32 }}>
      <Title2 as="h2" style={{ marginBottom: 16 }}>
        {t('onboarding.wizard.intro.title')}
      </Title2>

      <Body1
        block
        style={{ marginBottom: 20 }}
        dangerouslySetInnerHTML={{ __html: t('onboarding.wizard.intro.lead') }}
      />

      <Title3 as="h3" style={{ marginTop: 16, marginBottom: 8 }}>{t('onboarding.wizard.intro.willHappenTitle')}</Title3>
      <ol style={{ paddingLeft: 20, lineHeight: 1.8, color: tokens.colorNeutralForeground2 }}>
        <li>{t('onboarding.wizard.intro.willHappen1')}</li>
        <li dangerouslySetInnerHTML={{ __html: t('onboarding.wizard.intro.willHappen2') }} />
        <li>{t('onboarding.wizard.intro.willHappen3')}</li>
        <li>{t('onboarding.wizard.intro.willHappen4')}</li>
      </ol>

      <div style={{
        background: tokens.colorNeutralBackground2,
        padding: 16, borderRadius: 8, marginTop: 20,
        borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
      }}>
        <Body1 block style={{ fontWeight: 600, marginBottom: 4 }}>
          {t('onboarding.wizard.intro.noRiskTitle')}
        </Body1>
        <Caption1
          style={{ color: tokens.colorNeutralForeground2 }}
          dangerouslySetInnerHTML={{ __html: t('onboarding.wizard.intro.noRiskBody') }}
        />
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <Button
          appearance="primary" size="large"
          onClick={onStart} disabled={busy}
          icon={busy ? <Spinner size="tiny" /> : undefined}
        >
          {busy
            ? t('onboarding.wizard.intro.preparing')
            : (status?.connected
                ? t('onboarding.wizard.intro.ctaRefresh')
                : t('onboarding.wizard.intro.cta'))}
        </Button>
      </div>

      {status?.connected && (
        <div style={{
          marginTop: 16,
          padding: 12,
          background: tokens.colorPaletteGreenBackground1,
          borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}`,
          borderRadius: 6,
        }}>
          <Body1 block style={{ color: tokens.colorPaletteGreenForeground2, fontWeight: 600, marginBottom: 4 }}>
            {t('onboarding.wizard.intro.alreadyConnected', { name: status.organizationName ?? status.tenantId })}
          </Body1>
          <Caption1 style={{ color: tokens.colorNeutralForeground2, display: 'block' }}>
            {t('onboarding.wizard.intro.tenantLockedHint')}
          </Caption1>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Etape 2 - Consent (en attente du retour MS)
// ---------------------------------------------------------------------------
function ConsentStep({ onTest, busy }: { onTest: () => void; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: 32 }}>
      <Title2 as="h2" style={{ marginBottom: 16 }}>
        {t('onboarding.wizard.consent.title')}
      </Title2>

      <Body1
        block
        style={{ marginBottom: 16 }}
        dangerouslySetInnerHTML={{ __html: t('onboarding.wizard.consent.line1') }}
      />

      <Body1 block style={{ marginBottom: 16, color: tokens.colorNeutralForeground2 }}>
        {t('onboarding.wizard.consent.line2')}
      </Body1>

      <MessageBar intent="info">
        <MessageBarBody>
          {t('onboarding.wizard.consent.popupHint')}
        </MessageBarBody>
      </MessageBar>

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <Button
          appearance="primary"
          onClick={onTest} disabled={busy}
          icon={busy ? <Spinner size="tiny" /> : undefined}
        >
          {t('onboarding.wizard.consent.cta')}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Etape 3 - Test connection
// ---------------------------------------------------------------------------
function TestStep({ onTest, busy, test }: { onTest: () => void; busy: boolean; test: TestConnectionResponse | null }) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: 32 }}>
      <Title2 as="h2" style={{ marginBottom: 16 }}>
        {t('onboarding.wizard.test.title')}
      </Title2>

      {!test && !busy && (
        <>
          <Body1 block style={{ marginBottom: 16 }}>
            {t('onboarding.wizard.test.body')}
          </Body1>
          <Button appearance="primary" onClick={onTest}>
            {t('onboarding.wizard.test.cta')}
          </Button>
        </>
      )}

      {busy && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spinner size="large" label={t('onboarding.wizard.test.running')} />
        </div>
      )}

      {test && !test.success && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{t('onboarding.wizard.test.failTitle')}</MessageBarTitle>
            {test.error}
            {test.hint && <div style={{ marginTop: 8, fontStyle: 'italic' }}>{test.hint}</div>}
          </MessageBarBody>
        </MessageBar>
      )}

      {test && !test.success && (
        <div style={{ marginTop: 16 }}>
          <Button appearance="primary" onClick={onTest}>{t('onboarding.wizard.test.retry')}</Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Etape 4 - Done
// ---------------------------------------------------------------------------
function DoneStep({ status, onGoToDashboard }: { status: OnboardingStatus | null; onGoToDashboard: () => void }) {
  const { t, i18n } = useTranslation();
  // Encart EXO/Purview script retire 2026.06 - migration Graph cf. Phase 3.

  return (
    <Card style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>

      <Title2 as="h2" style={{ marginBottom: 12 }}>
        {t('onboarding.wizard.done.title')}
      </Title2>

      <Body1
        block
        style={{ marginBottom: 20, color: tokens.colorNeutralForeground2 }}
        dangerouslySetInnerHTML={{
          __html: t('onboarding.wizard.done.body', { name: status?.organizationName ?? status?.tenantId ?? '' }),
        }}
      />

      {status?.lastTestAt && (
        <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, marginBottom: 24 }}>
          {t('onboarding.wizard.done.lastVerified', { date: new Date(status.lastTestAt).toLocaleString(i18n.language) })}
        </Caption1>
      )}

      <Button appearance="primary" size="large" onClick={onGoToDashboard}>
        {t('onboarding.wizard.done.cta')}
      </Button>

      {/* Encart EXO/Purview script retire 2026.06 (migration Graph - Phase 3). */}
    </Card>
  );
}
